/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Chip,
  CircularProgress,
  InputAdornment,
  ListSubheader,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { KeywordWithTranslation } from 'components/Common/ArticleSelector/useKeywordsTranslation'
import { useController } from 'customHooks/useController'
import { bomItemWorkingStepsSelector } from 'features/BillOfMaterials/store/selectors/bomItemWorkingStepsSelector'
import _, { flatten, isEqual } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { Key } from 'utils/keyCodes'
import { useOrganizationContext } from 'utils/useOrganizationContext'
import {
  MaterialsController,
  MaterialsOperations,
} from '../../controllers/MaterialsController'
import {
  KeywordDto,
  MaterialSummaryDto,
} from '../../services/APIs/InternalAPI/internal-api.contracts'
import { ErrorMessage } from '../../store/Application/appActions'
import './MaterialKeywordsInput.scss'

type MaterialKeywordsInputProps = {
  selectedTokens: MaterialSummaryDto['tokens']
  onChangeTokens: (tokens: MaterialSummaryDto['tokens']) => Promise<void> | void
  tabIndex?: number
  autoFocus?: boolean
  groupOrderFilter?: string[]
  onMaterialsReceived?: (materials: MaterialSummaryDto[]) => void
  disabled?: boolean
  onLoading?: (loading) => void
  projectId?: string
  validationMessage?: string
  articleDescription?: string
  readOnly?: boolean
  enableSaveOnChange?: boolean
  savingKeywords?: boolean
  isPurchasingParts?: boolean
  error?: boolean
  disableLabel?: boolean
  onBlur?: () => void
  startOpen?: boolean
  disableUnderline?: boolean
  showGroups?: string[]
  rootMaterialKeywordsInputClassName?: string
  bomItemId?: string
}

export type MaterialKeywordsRefHandle = {
  setOpen: (open: boolean) => void
}

const debouncedTokenChange = _.debounce(
  (tokens: string[], onChange: (tokens: string[]) => void | Promise<void>) => {
    onChange(tokens)
  },
  2000
)

const MaterialKeywordsOptions = (option, state) => {
  const matches = match(option.translatedString, state.inputValue)
  const parts = parse(option.translatedString, matches)

  return (
    <div>
      {parts.map((part, index) => (
        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
          {part.text}
        </span>
      ))}
    </div>
  )
}

function applyTranslationToKeyword(
  t: TFunction<'keywords'>,
  keyword: KeywordDto,
  group: string
) {
  if (!keyword) return null

  return {
    ...keyword,
    translatedString: keyword.translationKey
      ? t(`keywords:${keyword.translationKey}`, {
          value: keyword.translationArgument,
        })
      : keyword.originalKeyword,
    group: group,
  }
}

function applyTranslationToKeywordsObject(
  t: TFunction<'keywords'>,
  tokens: Record<string, KeywordDto[]>
) {
  if (!tokens) return null

  const tokensWithTranslation = {}

  Object.keys(tokens).forEach((group) => {
    tokensWithTranslation[group] = tokens[group].map((keyword) =>
      applyTranslationToKeyword(t, keyword, group)
    )
  })

  return tokensWithTranslation
}

const MaterialKeywordsInput: React.ForwardRefRenderFunction<
  MaterialKeywordsRefHandle,
  MaterialKeywordsInputProps
> = (props: MaterialKeywordsInputProps, forwardedRef) => {
  const theme = useTheme()
  const { organizationId } = useOrganizationContext()
  const { controller, loading } = useController(
    () => new MaterialsController(organizationId)
  )
  const { t, i18n } = useTranslation('keywords')

  const workingSteps = useAppSelector(
    bomItemWorkingStepsSelector({ bomItemId: props.bomItemId }),
    isEqual
  )

  const initialTokens = React.useRef({})

  const [selectedTokens, setSelectedTokens] = useState<
    Record<string, KeywordWithTranslation[]>
  >(applyTranslationToKeywordsObject(t, props.selectedTokens))

  const [open, setOpen] = useState(props.startOpen || false)

  useEffect(() => {
    if (props.startOpen) {
      // getMaterialTokens(props.selectedTokens)
      getMaterialTokens(_.flatten(Object.values(props.selectedTokens)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [validationMessage, setValidationMessage] = useState<string>(
    props.validationMessage
  )

  useEffect(() => {
    if (!open || !initialTokens.current) {
      initialTokens.current = applyTranslationToKeywordsObject(
        t,
        props.selectedTokens
      )
      setSelectedTokens(initialTokens.current)
    }
  }, [open, props.selectedTokens, t])

  useEffect(() => {
    setValidationMessage(props.validationMessage)
  }, [props.validationMessage])

  const [availableTokens, setAvailableTokens] =
    useState<Array<KeywordWithTranslation>>()

  const getMaterialTokens = useCallback(
    async (_selectedTokens: KeywordDto[]) => {
      try {
        const workingStepList = workingSteps
          ? workingSteps.map(w => w.primaryWorkingStep)
          : []

        const response = await controller.GetMaterialsAsync(
          _selectedTokens.map((x) => x.originalKeyword),
          props.groupOrderFilter
            ? props.groupOrderFilter[_selectedTokens?.length]
            : null,
          props.projectId,
          props.isPurchasingParts,
          workingStepList,
          props.showGroups
        )

        if (response.materials && props.onMaterialsReceived) {
          props.onMaterialsReceived(response.materials)
        }

        const availableTokens: KeywordWithTranslation[] = []

        Object.keys(response.selectableTokens).forEach((group) => {
          response.selectableTokens[group].forEach((token) => {
            availableTokens.push({
              ...token,
              translatedString: t(`keywords:${token.translationKey}`, {
                value: token.translationArgument,
              }),
            })
          })
        })

        const sortedTokens = _.uniqBy(
          availableTokens.concat(flatten(Object.values(selectedTokens))),
          'originalKeyword'
        )
          .sort((a, b) =>
            a.translatedString.localeCompare(
              b.translatedString,
              i18n.language,
              { numeric: true }
            )
          )
          .sort((a, b) => a.category.localeCompare(b.category, i18n.language))

        setAvailableTokens(sortedTokens)
      } catch (err) {
        ErrorMessage(
          'material tokens',
          'unable to refresh material tokens\n' + err['message']
        )
      }
    },
    [controller, i18n.language, props, selectedTokens, t, workingSteps]
  )

  useEffect(() => {
    props.onLoading &&
      props.onLoading(loading[MaterialsOperations.GetMaterials] ?? false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const handleOpen = async () => {
    debouncedTokenChange.cancel()
    setOpen(true)
    await getMaterialTokens(_.flatten(Object.values(selectedTokens)))
  }

  const handleClose = () => {
    !props.enableSaveOnChange && props.onChangeTokens(selectedTokens)
    props.onBlur && props.onBlur()
    setOpen(false)
    setSelectedTokens(selectedTokens)
    initialTokens.current = selectedTokens
  }

  const handleTokensChanged = async (values: any) => {
    // console.log('handleTokensChanged', typeof values)
    if (typeof values === 'string') {
      setOpen(true)
      return
    }

    if (typeof values === 'object') {
      const newSelectedTokens: Record<string, KeywordWithTranslation[]> = {}

      values.forEach((value) => {
        newSelectedTokens[value.category] = [
          ...(newSelectedTokens[value.category] ?? []),
          value,
        ]
      })

      setSelectedTokens(newSelectedTokens)
      setAvailableTokens([])

      if (open) {
        if (props.enableSaveOnChange) {
          props.onChangeTokens(newSelectedTokens)
        }

        await getMaterialTokens(values)
      } else if (!open) {
        setOpen(true)
        // debouncedTokenChange(GroupedTokensToStringArray(newSelectedTokens), props.onChangeTokens)
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === Key.Escape) {
      setSelectedTokens(initialTokens.current)
      setOpen(false)
      setAvailableTokens([])
    }
  }

  React.useImperativeHandle(forwardedRef, () => ({
    setOpen: (open) => {
      setOpen(open)
    },
  }))

  return (
    <Autocomplete
      className="material-keywords-input"
      value={_.flatten(Object.values(selectedTokens)) || []}
      options={availableTokens || []}
      // options={
      //   availableTokens,
      // // .sort((a, b) => (a.group < b.group ? -1 : 1))}
      open={!props.readOnly && open}
      loadingText={`${t('common:loading')}...`}
      classes={{
        root: props.rootMaterialKeywordsInputClassName,
      }}
      disabled={props.disabled}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={handleClose}
      onChange={(e, value) => handleTokensChanged(value)}
      onKeyDown={handleKeyDown}
      noOptionsText={t(
        'common:material-tokens-not-found',
        'no materials keywords available'
      )}
      loading={loading[MaterialsOperations.GetMaterials]}
      groupBy={(option) => option.category}
      getOptionLabel={(option) => {
        return option.translatedString
      }}
      disableClearable
      handleHomeEndKeys
      openOnFocus
      disableCloseOnSelect
      fullWidth
      autoHighlight
      autoSelect
      autoComplete
      multiple
      filterSelectedOptions
      getOptionSelected={(option, value) => {
        return option.originalKeyword === value.originalKeyword
      }}
      // size="small"
      renderOption={MaterialKeywordsOptions}
      renderTags={(options) => {
        return options.map((token) => {
          return (
            <Chip
              key={token.originalKeyword}
              size="small"
              variant="outlined"
              label={token.translatedString}
              style={{ margin: '4px' }}
              onDelete={undefined}
              data-testid={'keyword'}
            />
          )
        })
      }}
      selectOnFocus
      renderGroup={(params) => {
        return [
          <ListSubheader
            key={params.key}
            style={{
              zIndex: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {t(`project:material-category-${params.group}`, params.group)}
          </ListSubheader>,
          params.children,
        ]
      }}
      renderInput={(inputProps) => {
        return (
          <TextField
            {...inputProps}
            data-testid={`materialSummary.keywords`}
            value={props.selectedTokens || {}}
            variant="standard"
            label={
              !props.disableLabel &&
              t('common:material-keywords', 'material keywords')
            }
            autoFocus={props.autoFocus}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              ...inputProps.InputProps,
              fullWidth: true,
              disableUnderline: props.disableUnderline || props.readOnly,
              endAdornment: (
                <>
                  {props.savingKeywords && (
                    <InputAdornment position="end">
                      <CircularProgress
                        disableShrink
                        size={12}
                        style={{ marginRight: '1em' }}
                      />
                    </InputAdornment>
                  )}
                  {inputProps.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...inputProps.inputProps,
              tabIndex: props.tabIndex,
              readOnly: props.readOnly,
            }}
            error={Boolean(validationMessage) || props.error}
            disabled={
              loading[MaterialsOperations.GetMaterials] ||
              props.disabled ||
              props.savingKeywords
            }
            helperText={
              <>
                {props.articleDescription &&
                  props.articleDescription !== 'not set' && (
                    <Typography
                      component="span"
                      variant="caption"
                      style={{ whiteSpace: 'normal' }}
                    >
                      {props.articleDescription}
                    </Typography>
                  )}
                {validationMessage && (
                  <Typography variant="caption" color="error">
                    {validationMessage}
                  </Typography>
                )}
              </>
            }
          />
        )
      }}
    />
  )
}

const MaterialInputWithRef = React.forwardRef(MaterialKeywordsInput)

export { MaterialInputWithRef as MaterialKeywordsInput }
